import React, { useCallback, useRef } from "react";

import { CircleCross } from "@/assets/icons";
import { NUMBER_WITH_COMMA_REGEX } from "@/constants/regex";
import { cn } from "@/lib/utils";

import { twMerge } from "tailwind-merge";

interface InputWithClearProps {
  value: string;
  onClick?: (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange: (value: string) => void;
  /** Type of `inputMode` for the underlying `<input />` element */
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  placeholder?: string;
  ariaLabel?: string;
  dataTestId?: string;
  error?: boolean;
  disabled?: boolean;
  /** Whether to render the trailing button that resets the input to zero. */
  showClearButton?: boolean;
  /** Allow a leading minus sign so negative numbers can be entered. */
  allowNegative?: boolean;
  className?: string;
  inputClassName?: string;
}

// Input with clear button component
export const InputWithClear = React.memo(
  ({
    value,
    onClick,
    onBlur,
    onChange,
    inputMode = "numeric",
    placeholder = "",
    ariaLabel = "",
    dataTestId,
    className,
    error = false,
    disabled = false,
    showClearButton = true,
    allowNegative = false,
    inputClassName
  }: InputWithClearProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        // Allow empty input
        if (newValue === "") {
          onChange(newValue);
          return;
        }

        // Allow a lone minus sign mid-typing when negatives are permitted.
        if (allowNegative && newValue === "-") {
          onChange(newValue);
          return;
        }

        // The optional leading minus does not affect the digit/comma/decimal
        // rules, so validate the unsigned part.
        const isNegative = allowNegative && newValue.startsWith("-");
        const unsigned = isNegative ? newValue.slice(1) : newValue;

        // Basic character validation to only allow digits, commas, and one decimal point
        if (!NUMBER_WITH_COMMA_REGEX.test(unsigned)) {
          return;
        }

        // Count the number of decimal points
        const decimalCount = (unsigned.match(/\./g) || []).length;

        if (decimalCount > 1) {
          return;
        }

        // Check for proper comma placement (should be every 3 digits in the integer part)
        const parts = unsigned.split(".");
        const integerPart = parts[0];

        // Allow partial inputs during typing
        // This is a more permissive check during typing
        if (integerPart.includes(",")) {
          const groups = integerPart.split(",");

          // Check if any group (except possibly the first) doesn't have exactly 3 digits
          // But allow incomplete groups for the last section during typing
          for (let i = 1; i < groups.length; i++) {
            // Last group can be incomplete during typing
            if (i === groups.length - 1 && groups[i].length <= 3) {
              continue;
            }

            if (groups[i].length !== 3) {
              return;
            }
          }
        }

        onChange(newValue);
      },
      [onChange, allowNegative]
    );

    // Format the number properly when the input loses focus
    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        if (value) {
          try {
            // Remove all commas first
            const normalizedValue = value.replaceAll(",", "");

            // Preserve a leading minus, but group/format only the unsigned part so
            // the "-" is not counted when inserting thousands separators.
            const isNegative = allowNegative && normalizedValue.startsWith("-");
            const unsigned = isNegative
              ? normalizedValue.slice(1)
              : normalizedValue;

            // Split into integer and decimal parts
            const parts = unsigned.split(".");
            const integerPart = parts[0];
            const decimalPart = parts.length > 1 ? parts[1] : "";

            // Format integer part with commas
            let formattedInteger = "";
            for (let i = 0; i < integerPart.length; i++) {
              if (i > 0 && (integerPart.length - i) % 3 === 0) {
                formattedInteger += ",";
              }
              formattedInteger += integerPart[i];
            }

            // Combine integer and decimal parts
            const formattedValue = decimalPart
              ? `${formattedInteger}.${decimalPart}`
              : formattedInteger;

            onChange(isNegative ? `-${formattedValue}` : formattedValue);
          } catch (error) {
            // If any error occurs during formatting, keep the original value
            console.error("Error formatting number:", error);
          }
        }

        onBlur?.(event);
      },
      [value, onChange, allowNegative, onBlur]
    );

    const handleClear = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange("0");
        inputRef.current?.focus();
      },
      [onChange]
    );

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    return (
      <div
        className={twMerge(
          "flex items-center border rounded-2xl px-3 py-2 w-full",
          error
            ? "border-red-500"
            : "border-gainsboro focus-within:border-teal-500",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
      >
        <input
          onClick={onClick}
          ref={inputRef}
          className={cn(
            "w-full focus:outline-none text-sm",
            disabled && "cursor-not-allowed bg-transparent",
            inputClassName
          )}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={10}
          aria-label={ariaLabel}
          inputMode={inputMode}
          data-testid={dataTestId}
          disabled={disabled}
        />
        {showClearButton && value && !disabled && (
          <button
            className="bg-transparent border-0 p-0"
            onClick={handleClear}
            type="button"
            onMouseDown={handleMouseDown}
          >
            <CircleCross className="fill-dim-gray w-5 h-5" />
          </button>
        )}
      </div>
    );
  }
);
